import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/auth';
import { usePresetStore } from "../../components/datastore.js";
import useLogout from '../../utils/logout';
import useAxios from '../../utils/useAxios';
import { useUkIhtVisible } from '../../utils/useUserCountry';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import LoginForm from '../../views/login';
import ResetForm from '../../views/reset';
import RegisterForm from '../../views/register';
import FeedbackForm from '../../views/feedback';
import LogoBanner from "../../components/logobanner.png";
import { FaBars, FaTimes, FaChevronDown } from 'react-icons/fa';
import Cookies from 'js-cookie';
import Form from 'react-bootstrap/Form';
import { GoogleLogin } from '@react-oauth/google';

const Navigbar = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const logInState = useAuthStore().isLoggedIn();
  const userId = useAuthStore().user();
  const ukIhtVisible = useUkIhtVisible();
  const resetPresetObject = usePresetStore((state) => state.resetPresetObject);
  const resetPlanId = usePresetStore((state) => state.resetPlanId);
  const resetGuestMode = usePresetStore((state) => state.resetGuestMode);
  const { logout } = useLogout();
  const api = useAxios();
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [marketingUpdating, setMarketingUpdating] = useState(false);

  // Rendered here (rather than inside LoginForm) so it still shows after
  // a successful login closes the login modal — see login.jsx.
  const showLoginWelcomeModal = useAuthStore((state) => state.showLoginWelcomeModal);
  const setShowLoginWelcomeModal = useAuthStore((state) => state.setShowLoginWelcomeModal);
  const loginWelcomeVariant = useAuthStore((state) => state.loginWelcomeVariant);
  const setLoginWelcomeVariant = useAuthStore((state) => state.setLoginWelcomeVariant);

  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [logoutMessage, setLogoutMessage] = useState('');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [toolsOpen, setToolsOpen] = useState(false);
  const [learnOpen, setLearnOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const [mobileLearnOpen, setMobileLearnOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);

  const toolsRef = useRef(null);
  const learnRef = useRef(null);
  const aboutRef = useRef(null);
  const accountRef = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target)) setToolsOpen(false);
      if (learnRef.current && !learnRef.current.contains(e.target)) setLearnOpen(false);
      if (aboutRef.current && !aboutRef.current.contains(e.target)) setAboutOpen(false);
      if (accountRef.current && !accountRef.current.contains(e.target)) setAccountOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); setToolsOpen(false); setLearnOpen(false); setAboutOpen(false); setAccountOpen(false); }, [location]);

  // Load the user's current marketing-email preference once logged in, so
  // the account-menu checkbox reflects the stored value rather than always
  // starting unticked.
  useEffect(() => {
    if (!logInState) return;
    let cancelled = false;
    api.get('/userinformation/')
      .then((response) => { if (!cancelled) setMarketingOptIn(!!response.data?.userinformation?.marketing_opt_in); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logInState]);

  const handleMarketingOptInChange = async (e) => {
    const next = e.target.checked;
    setMarketingOptIn(next); // Optimistic — the checkbox should feel instant
    setMarketingUpdating(true);
    try {
      await api.post('/marketing-preference/', { marketing_opt_in: next });
    } catch (error) {
      setMarketingOptIn(!next); // Revert on failure
    } finally {
      setMarketingUpdating(false);
    }
  };

  // Auto-open the login modal when linked to with ?login=1 (e.g. from the
  // "registration approved" email) so the user lands straight on the form.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("login") === "1" && !logInState) {
      setShowLoginModal(true);
    }
  }, [logInState]);

  const handleLogout = async () => {
    // Await logout() so the auth store's user is cleared (logInState
    // becomes false) before planId/presetObject are reset. Otherwise
    // resetPlanId() clears planId while logInState is still stale-true,
    // which races calculator.js's entry-flow effect into firing and
    // popping the "create/open a plan" modal on top of this logout notice.
    await logout();
    resetPlanId();
    resetPresetObject();
    resetGuestMode();
    setLogoutMessage('You have been logged out');
    setShowLogoutModal(true);
    setAccountOpen(false);
  };

  const handleCloseLogout = () => { setShowLogoutModal(false); window.location.reload(); };

  useEffect(() => {
    if (!showLogoutModal) return;
    const timer = setTimeout(handleCloseLogout, 2000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLogoutModal]);

  const openFeedbackModal = () => {
    setShowFeedbackModal(true);
    setAccountOpen(false);
    setMobileOpen(false);
  };

  const openDeleteModal = () => {
    setDeletePassword('');
    setDeleteError('');
    setShowDeleteModal(true);
    setAccountOpen(false);
    setMobileOpen(false);
  };

  const closeDeleteModal = () => {
    if (deleteLoading) return; // Don't let the request be interrupted mid-flight
    setShowDeleteModal(false);
    setDeletePassword('');
    setDeleteError('');
  };

  // Shared by both confirmation paths (password or a fresh Google sign-in)
  // below — same request, same success/cleanup, just a different body.
  const submitDeleteAccount = async (body) => {
    setDeleteError('');
    setDeleteLoading(true);
    try {
      await api.post('/delete-account/', body);
      // The account (and every plan/setting tied to it) is gone server-side
      // at this point — mirror useLogout's client-side cleanup so the app
      // doesn't keep believing this user is signed in.
      Cookies.remove('access');
      Cookies.remove('refresh');
      useAuthStore.getState().setUser(null);
      resetPlanId();
      resetPresetObject();
      resetGuestMode();
      setShowDeleteModal(false);
      setLogoutMessage('Your account and all associated data have been deleted.');
      setShowLogoutModal(true);
    } catch (error) {
      setDeleteError(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteAccount = (e) => {
    e.preventDefault();
    submitDeleteAccount({ password: deletePassword });
  };

  // For password-less (Google-only) accounts — re-confirm with a fresh
  // Google sign-in instead of a password they don't have.
  const handleGoogleDeleteConfirm = (credentialResponse) => {
    submitDeleteAccount({ credential: credentialResponse.credential });
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const avatarLetter = logInState
    ? (userId?.customer_name || userId?.username || '?')[0].toUpperCase()
    : '?';

  // The UK inheritance tax guide is hidden from the menu for visitors located outside the UK,
  // unless the admin setting shows it to everyone (useUkIhtVisible). The page itself stays
  // reachable by URL.
  const learnItems = [
    { to: '/methodology', label: 'RetireSecure Methodology & Data' },
    { to: '/returns', label: 'Historic Asset Returns' },
    { to: '/forwardreturns', label: 'Forward Asset Returns' },
    { to: '/research', label: 'Decumulation Strategies' },
    { to: '/withdrawal', label: 'Withdrawal Approaches' },
    { to: '/inheritancetax', label: 'Inheritance Tax Mitigation', ukOnly: true },
    { to: '/links', label: 'Links' },
  ].filter(item => !item.ukOnly || ukIhtVisible);

  const toolsItems = [
    { to: '/quicktest', label: 'Quick Test' },
    { to: '/decumulation', label: 'Strategy Comparison' },
    { to: '/calc', label: 'Full Plan Simulator' },
    { to: '/ladder', label: 'Bond Ladder' },
  ];

  const aboutItems = [
    { to: '/whoisitfor', label: 'Who RetireSecure Is For' },
    { to: '/about', label: 'About Us' },
    { to: '/terms', label: 'Terms' },
    { to: '/privacy', label: 'Privacy Policy' },
  ];

  return (
    <>
      <nav className="rs-navbar">
        <div className="rs-navbar-inner">

          {/* Left: Logo — hidden on home page where the hero logo already shows */}
          {location.pathname !== '/' && (
            <Link to="/" className="rs-navbar-logo">
              <img src={LogoBanner} alt="RetireSecure" />
            </Link>
          )}

          {/* Right: primary nav (desktop only) */}
          <div className="rs-navbar-centre">
            {/* Tools dropdown */}
            <div className="rs-nav-dropdown-wrap" ref={toolsRef}>
              <button
                className={`rs-nav-link rs-nav-dropdown-trigger${toolsItems.some(i => isActive(i.to)) ? ' active' : ''}`}
                onClick={() => setToolsOpen(o => !o)}
                aria-expanded={toolsOpen}
              >
                Tools <FaChevronDown className={`rs-chevron${toolsOpen ? ' open' : ''}`} />
              </button>
              {toolsOpen && (
                <div className="rs-dropdown-menu">
                  {toolsItems.map(item => (
                    <Link key={item.to} to={item.to} className="rs-dropdown-item" onClick={() => setToolsOpen(false)}>
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Learn dropdown */}
            <div className="rs-nav-dropdown-wrap" ref={learnRef}>
              <button
                className={`rs-nav-link rs-nav-dropdown-trigger${learnItems.some(i => isActive(i.to)) ? ' active' : ''}`}
                onClick={() => setLearnOpen(o => !o)}
                aria-expanded={learnOpen}
              >
                Learn <FaChevronDown className={`rs-chevron${learnOpen ? ' open' : ''}`} />
              </button>
              {learnOpen && (
                <div className="rs-dropdown-menu">
                  {learnItems.map(item => (
                    <Link key={item.to} to={item.to} className="rs-dropdown-item" onClick={() => setLearnOpen(false)}>
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* About dropdown */}
            <div className="rs-nav-dropdown-wrap" ref={aboutRef}>
              <button
                className={`rs-nav-link rs-nav-dropdown-trigger${aboutItems.some(i => isActive(i.to)) ? ' active' : ''}`}
                onClick={() => setAboutOpen(o => !o)}
                aria-expanded={aboutOpen}
              >
                About <FaChevronDown className={`rs-chevron${aboutOpen ? ' open' : ''}`} />
              </button>
              {aboutOpen && (
                <div className="rs-dropdown-menu">
                  {aboutItems.map(item => (
                    <Link key={item.to} to={item.to} className="rs-dropdown-item" onClick={() => setAboutOpen(false)}>
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right: account */}
          <div className="rs-navbar-right">
            <div className="rs-nav-dropdown-wrap" ref={accountRef}>
              {logInState ? (
                <>
                  <button
                    className="rs-avatar-btn"
                    onClick={() => setAccountOpen(o => !o)}
                    aria-label="Account menu"
                  >
                    {avatarLetter}
                  </button>
                  {accountOpen && (
                    <div className="rs-dropdown-menu rs-dropdown-menu--right">
                      <div className="rs-dropdown-username">{userId?.username}</div>
                      <div className="rs-dropdown-divider" />
                      <label className="rs-dropdown-checkbox-item">
                        <input
                          type="checkbox"
                          checked={marketingOptIn}
                          disabled={marketingUpdating}
                          onChange={handleMarketingOptInChange}
                        />
                        <span>Send me occasional emails about the service, new features, updates and other relevant information</span>
                      </label>
                      <div className="rs-dropdown-divider" />
                      <button className="rs-dropdown-item" onClick={openFeedbackModal}>Give feedback</button>
                      <div className="rs-dropdown-divider" />
                      <button className="rs-dropdown-item" onClick={handleLogout}>Log out</button>
                      <button className="rs-dropdown-item rs-dropdown-item--danger" onClick={openDeleteModal}>Delete account</button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <button className="rs-login-btn" onClick={() => setShowLoginModal(true)}>Log In</button>
                  <button className="rs-register-btn" onClick={() => setShowRegisterModal(true)}>Register</button>
                </>
              )}
            </div>

            {/* Mobile hamburger */}
            <button
              className="rs-hamburger"
              onClick={() => setMobileOpen(o => !o)}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="rs-mobile-menu">
            <Link to="/" className="rs-mobile-link">Introduction</Link>
            <button className="rs-mobile-link rs-mobile-section-toggle" onClick={() => setMobileToolsOpen(o => !o)}>
              Tools <FaChevronDown className={`rs-chevron${mobileToolsOpen ? ' open' : ''}`} />
            </button>
            {mobileToolsOpen && toolsItems.map(item => (
              <Link key={item.to} to={item.to} className={`rs-mobile-link rs-mobile-sub${isActive(item.to) ? ' active' : ''}`}>{item.label}</Link>
            ))}

            <button className="rs-mobile-link rs-mobile-section-toggle" onClick={() => setMobileLearnOpen(o => !o)}>
              Learn <FaChevronDown className={`rs-chevron${mobileLearnOpen ? ' open' : ''}`} />
            </button>
            {mobileLearnOpen && learnItems.map(item => (
              <Link key={item.to} to={item.to} className="rs-mobile-link rs-mobile-sub">{item.label}</Link>
            ))}

            <button className="rs-mobile-link rs-mobile-section-toggle" onClick={() => setMobileAboutOpen(o => !o)}>
              About <FaChevronDown className={`rs-chevron${mobileAboutOpen ? ' open' : ''}`} />
            </button>
            {mobileAboutOpen && aboutItems.map(item => (
              <Link key={item.to} to={item.to} className="rs-mobile-link rs-mobile-sub">{item.label}</Link>
            ))}

            <div className="rs-mobile-divider" />
            {logInState ? (
              <>
                <div className="rs-mobile-username">{userId?.username}</div>
                <label className="rs-mobile-link rs-dropdown-checkbox-item">
                  <input
                    type="checkbox"
                    checked={marketingOptIn}
                    disabled={marketingUpdating}
                    onChange={handleMarketingOptInChange}
                  />
                  <span>Send me occasional emails about the service, new features, updates and other relevant information</span>
                </label>
                <button className="rs-mobile-link" onClick={openFeedbackModal}>Give feedback</button>
                <button className="rs-mobile-link" onClick={handleLogout}>Log out</button>
                <button className="rs-mobile-link rs-dropdown-item--danger" onClick={openDeleteModal}>Delete account</button>
              </>
            ) : (
              <>
                <button className="rs-mobile-link" onClick={() => { setMobileOpen(false); setShowLoginModal(true); }}>Log In</button>
                <button className="rs-mobile-link" onClick={() => { setMobileOpen(false); setShowRegisterModal(true); }}>Register</button>
                <button className="rs-mobile-link" onClick={() => { setMobileOpen(false); setShowResetModal(true); }}>Reset password</button>
              </>
            )}
          </div>
        )}
      </nav>

      {/* Modals */}
      <Modal show={showLogoutModal} onHide={handleCloseLogout}>
        <Modal.Header><Modal.Title>Notice</Modal.Title></Modal.Header>
        <Modal.Body>{logoutMessage}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseLogout}>Continue</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showLoginModal} onHide={() => setShowLoginModal(false)}>
        <Modal.Header closeButton><Modal.Title>Log in</Modal.Title></Modal.Header>
        <Modal.Body>
          <LoginForm
            onClose={() => setShowLoginModal(false)}
            onForgotPassword={() => { setShowLoginModal(false); setShowResetModal(true); }}
          />
        </Modal.Body>
      </Modal>

      <Modal
        show={showLoginWelcomeModal}
        onHide={() => { setShowLoginWelcomeModal(false); setLoginWelcomeVariant('standard'); }}
      >
        <Modal.Header closeButton>
          <Modal.Title>{loginWelcomeVariant === 'new_google_account' ? 'Welcome' : 'Login'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loginWelcomeVariant === 'new_google_account' ? (
            <p>Welcome to RetireSecure! Your account has been created using your Google sign-in.</p>
          ) : (
            <p>You are logged in. Please note:</p>
          )}
          <ul>
            <li>Plans can be created and saved in Plan Simulator and Bond Ladder modes.</li>
            <li>Plans can be created and tested without saving in all modes.</li>
          </ul>
          <p>We'd love your feedback on how to improve the app — look for "Give feedback" in the account menu, top right.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="success"
            onClick={() => { setShowLoginWelcomeModal(false); setLoginWelcomeVariant('standard'); }}
          >
            Continue
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showFeedbackModal} onHide={() => setShowFeedbackModal(false)}>
        <Modal.Header closeButton><Modal.Title>Give feedback</Modal.Title></Modal.Header>
        <Modal.Body><FeedbackForm onClose={() => setShowFeedbackModal(false)} /></Modal.Body>
      </Modal>

      <Modal show={showResetModal} onHide={() => setShowResetModal(false)}>
        <Modal.Header closeButton><Modal.Title>Reset password</Modal.Title></Modal.Header>
        <Modal.Body><ResetForm onClose={() => setShowResetModal(false)} /></Modal.Body>
      </Modal>

      <Modal show={showRegisterModal} onHide={() => setShowRegisterModal(false)}>
        <Modal.Header closeButton><Modal.Title>Register</Modal.Title></Modal.Header>
        <Modal.Body><RegisterForm onClose={() => setShowRegisterModal(false)} /></Modal.Body>
      </Modal>

      <Modal show={showDeleteModal} onHide={closeDeleteModal}>
        <Modal.Header closeButton><Modal.Title>Delete account</Modal.Title></Modal.Header>
        {userId?.has_usable_password ? (
          <Form onSubmit={handleDeleteAccount}>
            <Modal.Body>
              <p>
                This permanently deletes your account and everything tied to it — saved plans,
                account settings and login history. This can't be undone.
              </p>
              <Form.Group controlId="deleteAccountPassword">
                <Form.Label className="small">Enter your password to confirm:</Form.Label>
                <Form.Control
                  type="password"
                  autoFocus
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                />
              </Form.Group>
              {deleteError && <div style={{ color: 'red', marginTop: '10px' }}>{deleteError}</div>}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={closeDeleteModal} disabled={deleteLoading}>Cancel</Button>
              <Button variant="danger" type="submit" disabled={deleteLoading || !deletePassword}>
                {deleteLoading ? 'Deleting…' : 'Permanently delete my account'}
              </Button>
            </Modal.Footer>
          </Form>
        ) : (
          <>
            <Modal.Body>
              <p>
                This permanently deletes your account and everything tied to it — saved plans,
                account settings and login history. This can't be undone.
              </p>
              <p className="small">Your account uses Google sign-in, so confirm by signing in with Google again:</p>
              <GoogleLogin
                text="continue_with"
                onSuccess={handleGoogleDeleteConfirm}
                onError={() => setDeleteError('Google confirmation failed. Please try again.')}
              />
              {deleteError && <div style={{ color: 'red', marginTop: '10px' }}>{deleteError}</div>}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={closeDeleteModal} disabled={deleteLoading}>Cancel</Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </>
  );
};

export default Navigbar;
